
import { DOCUMENT, Injectable, RendererStyleFlags2, inject } from '@angular/core';
import { PlatformService } from '../platform/platform.service';

export interface ColorConfigurations {
  themeColor: string;
  themeAccentColor: string;
  themeTextColor: string;
  themeBorderColor: string;
  themeTextVariant: string;
  themeSuccessVariant: string;
  // Homepage theme properties
  themeFontFamily?: string;
  themeBackgroundColor?: string;
}

export const DEFAULT_THEME: ColorConfigurations = {
  themeColor: '#ac0236',
  themeAccentColor: '#2bb3b3',
  themeTextColor: '#ac0236',
  themeBorderColor: '#ac0236',
  themeTextVariant: '#262e2e',
  themeSuccessVariant: '#4caf50'
};

export interface FontConfigurations {
  fontFamily: string;
  regularFontFileUrl: string;
  mediumFontFileUrl: string;
  boldFontFileUrl: string;
}

// keep this for reference
// export const DEFAULT_FONT: FontConfigurations = {
//   fontFamily: 'inter',
//   regularFontFileUrl: 'https://assets.hyperinvento.com/consumer_fonts/Inter-Regular.ttf',
//   mediumFontFileUrl: 'https://assets.hyperinvento.com/consumer_fonts/Inter-Medium.ttf',
//   boldFontFileUrl: 'https://assets.hyperinvento.com/consumer_fonts/Inter-Bold.ttf',
// }

export const DEFAULT_FONT: FontConfigurations = {
  fontFamily: 'Inter',
  regularFontFileUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400&display=swap',
  mediumFontFileUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@500&display=swap',
  boldFontFileUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@700&display=swap',
};


@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private _document = inject<Document>(DOCUMENT);
  private platformService = inject(PlatformService);


  colorConfigurations: ColorConfigurations = DEFAULT_THEME;

  private prepareThemeVariablesForBody(colorConfigurations: ColorConfigurations) {
    const {
      themeColor = DEFAULT_THEME.themeColor,
      themeAccentColor = DEFAULT_THEME.themeAccentColor,
      themeTextColor = DEFAULT_THEME.themeTextColor,
      themeTextVariant = DEFAULT_THEME.themeTextVariant,
      themeBorderColor = DEFAULT_THEME.themeBorderColor,
      themeSuccessVariant = DEFAULT_THEME.themeSuccessVariant
    } = colorConfigurations || {} as ColorConfigurations;

    return [
      { property: `--ThemeColor`, value: themeColor },
      { property: `--ThemeAccentVariant`, value: themeAccentColor },
      { property: `--ThemeTextVariant`, value: themeTextVariant },
      { property: `--ThemeTextColor`, value: themeTextColor },
      { property: `--ThemeBorderColor`, value: themeBorderColor },
      { property: '--ThemeSuccessVariant', value: themeSuccessVariant }
    ];
  }

  setThemeVariables(colorConfigurations: any, renderer: any) {

    // set style on body so that overlay can catch them
    this.prepareThemeVariablesForBody(colorConfigurations).forEach(style => {

      const { property, value } = style;
      renderer.setStyle(this._document.body, property, value, RendererStyleFlags2.DashCase)
    });

    // Handle homepage theme properties (like font family for header)
    if (colorConfigurations?.themeFontFamily) {
      // Apply to header theme container
      const headerThemeContainer = this._document.getElementsByClassName("dynamic-header-theme")[0];
      if (headerThemeContainer) {
        renderer.setStyle(headerThemeContainer, 'font-family', colorConfigurations.themeFontFamily);
      }

      // Apply to search input
      const searchInputContainer = this._document.getElementsByClassName("search-input")[0];
      if (searchInputContainer) {
        renderer.setStyle(searchInputContainer, 'font-family', colorConfigurations.themeFontFamily);
      }
    }

    // Handle homepage background color
    if (colorConfigurations?.themeBackgroundColor) {
      const themeContainers = this._document.getElementsByClassName("dynamic-theme-section");
      for (let i = 0; i < themeContainers.length; i++) {
        const themeContainer = themeContainers[i];
        renderer.setStyle(themeContainer, 'background-color', colorConfigurations.themeBackgroundColor);
      }
    }
  }


  private prepareFontVariablesForBody(fontConfigurations: FontConfigurations) {

    const defaultFont = "Inter"; // Default font
    const { fontFamily = defaultFont } = fontConfigurations || {};


    // Remove existing font style if it exists
    const existingStyle = this._document.getElementById('global-font-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Only load font if it's not Inter (since Inter is already loaded in styles.scss)
    if (fontFamily.toLowerCase() !== 'inter') {

      // Create a new style element
      const styleElement = this._document.createElement('style');
      styleElement.id = 'global-font-style';
      styleElement.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@100..900&display=swap');

      :root {
        --RegularFont: '${fontFamily}', sans-serif;
        --MediumFont: '${fontFamily}', sans-serif;
        --BoldFont: '${fontFamily}', sans-serif;
      }
    `;

      this._document.head.appendChild(styleElement);
    } else {

      // For Inter font, just set the CSS variables (Inter is already loaded in styles.scss)
      const styleElement = this._document.createElement('style');
      styleElement.id = 'global-font-style';
      styleElement.innerHTML = `
      :root {
        --RegularFont: 'Inter', sans-serif;
        --MediumFont: 'Inter', sans-serif;
        --BoldFont: 'Inter', sans-serif;
      }
    `;

      this._document.head.appendChild(styleElement);
    }

  }

  setFontVariables(fontConfigurations: any, _renderer: any) {
    // Apply font variables in both SSR and browser for proper rendering
    this.prepareFontVariablesForBody(fontConfigurations);
  }

}
